import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useChangePassword } from '../api/hooks';
import { Button, Card, ErrorText, Field, TextInput } from '../components/ui';
import { zh } from '../i18n';

/** Forced password change after a one-time password (CONTRACTS §4.1/§4.7). */
export function FirstRunRoute(): JSX.Element {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const changePassword = useChangePassword();
  const navigate = useNavigate();

  const onSubmit = (event: FormEvent): void => {
    event.preventDefault();
    if (newPassword.length < 12) {
      setLocalError(zh.firstRun.tooShort);
      return;
    }
    if (newPassword !== confirmPassword) {
      setLocalError(zh.firstRun.mismatch);
      return;
    }
    setLocalError(null);
    changePassword.mutate(
      { currentPassword, newPassword },
      { onSuccess: () => navigate('/', { replace: true }) },
    );
  };

  return (
    <div className="auth">
      <Card className="auth__card">
        <h1 className="auth__title">{zh.firstRun.title}</h1>
        <p className="small muted">{zh.firstRun.subtitle}</p>
        <form className="stack" onSubmit={onSubmit}>
          <Field label={zh.firstRun.currentPassword}>
            {(id) => (
              <TextInput
                id={id}
                type="password"
                autoComplete="current-password"
                value={currentPassword}
                onChange={(event) => setCurrentPassword(event.target.value)}
              />
            )}
          </Field>
          <Field label={zh.firstRun.newPassword}>
            {(id) => (
              <TextInput
                id={id}
                type="password"
                autoComplete="new-password"
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
              />
            )}
          </Field>
          <Field label={zh.firstRun.confirmPassword} error={localError}>
            {(id) => (
              <TextInput
                id={id}
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
              />
            )}
          </Field>
          <ErrorText error={changePassword.error} />
          <Button variant="primary" block type="submit" disabled={changePassword.isPending}>
            {changePassword.isPending ? zh.app.saving : zh.firstRun.submit}
          </Button>
        </form>
      </Card>
    </div>
  );
}
